import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Loader2, Settings, X, Check, MessageSquare } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API, ROLES } from './domainUtils';

export const DomainUsersTab = ({ domainId, domain, users, setUsers, token }) => {
  const [newUser, setNewUser] = useState({ extension: '', name: '', password: '', role: 'domain_agent', contact_email: '' });
  const [addingUser, setAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // full user object being edited
  const headers = { Authorization: `Bearer ${token}` };

  // Set of agent extensions in this domain — used by the supervisor
  // "assigned agents" checkbox picker so a supervisor can be locked to
  // just a slice of the domain's reps.
  const agentPool = useMemo(
    () => (users || []).filter(u => u.role === 'domain_agent' && u.extension),
    [users],
  );

  const handleAddUser = async () => {
    if (!newUser.extension || !newUser.name || !newUser.password) {
      toast.error('Extension, name and password are required');
      return;
    }
    setAddingUser(true);
    try {
      // 30 s hard timeout so a stalled network / worker crash can never
      // leave the UI stuck on "Add User" spinning forever.
      const r = await axios.post(
        `${API}/admin/domains/${domainId}/users`,
        newUser,
        { headers, timeout: 30000 },
      );
      if (!r?.data?.id) {
        throw new Error('Backend returned an empty user object');
      }
      // Guard against `users` prop ever being non-array (e.g. an API that
      // returns null); without the guard `[...u, r.data]` would throw
      // and the toast would say something unrelated.
      setUsers(u => (Array.isArray(u) ? [...u, r.data] : [r.data]));
      setNewUser({ extension: '', name: '', password: '', role: 'domain_agent' });
      toast.success(`User ${r.data.email} created`);
    } catch (e) {
      // Surface the real failure so the user can screenshot it instead of
      // seeing a mystery spinner. Network errors, timeouts, 4xx and 5xx
      // all funnel through here with useful context.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const netMsg = e?.code === 'ECONNABORTED'
        ? 'Request timed out after 30s — check your connection or try again'
        : e?.message;
      console.error('Add-user failed:', {
        status, detail, code: e?.code, message: e?.message,
        role: newUser.role, extension: newUser.extension,
      });
      toast.error(
        (typeof detail === 'string' && detail)
          || (status ? `Failed to add user (HTTP ${status})` : null)
          || netMsg
          || 'Failed to add user',
      );
    } finally {
      // Belt-and-braces: no code path should be able to leave the button
      // spinning. The finally always runs, even on synchronous throws.
      setAddingUser(false);
    }
  };

  const removeUser = async (userId, userEmail) => {
    if (!window.confirm(`Remove ${userEmail}?`)) return;
    try {
      await axios.delete(
        `${API}/admin/domains/${domainId}/users/${userId}`,
        { headers, timeout: 30000 },
      );
      setUsers(u => (Array.isArray(u) ? u.filter(x => x.id !== userId) : []));
      toast.success('User removed');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      console.error('Remove-user failed:', { status, detail, code: e?.code, message: e?.message });
      toast.error(
        (typeof detail === 'string' && detail)
          || (status ? `Failed to remove user (HTTP ${status})` : null)
          || 'Failed to remove user',
      );
    }
  };

  const toggleSmsAccess = async (u) => {
    const next = !u.sms_access;
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/users/${u.id}/sms-access`,
        { sms_access: next }, { headers, timeout: 20000 },
      );
      setUsers(prev => (Array.isArray(prev) ? prev.map(x => (x.id === u.id ? { ...x, sms_access: next } : x)) : prev));
      toast.success(next ? `SMS enabled for ${u.name}` : `SMS disabled for ${u.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update SMS access');
    }
  };

  const fields = [
    { key: 'extension', label: 'Extension *', placeholder: '9922' },
    { key: 'name', label: 'Full Name *', placeholder: 'John Smith' },
    { key: 'password', label: 'Password *', placeholder: '••••••••', type: 'password' },
    { key: 'contact_email', label: 'Contact email (optional)', placeholder: 'their.name@company.com', type: 'email' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Users className="w-4 h-4" /> Add Portal User
        </h3>
        <p className="text-white/30 text-xs mb-4">
          Login will be: <span className="text-teal-400 font-mono">{newUser.extension || 'ext'}@{domain?.yabbit_domain}</span>
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-xs text-white/40 block mb-1">{f.label}</label>
              <input
                type={f.type || 'text'}
                value={newUser[f.key]}
                onChange={e => setNewUser(u => ({ ...u, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                data-testid={`new-user-${f.key}`}
              />
            </div>
          ))}
          <div>
            <label className="text-xs text-white/40 block mb-1">Role</label>
            <select
              value={newUser.role}
              onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
              className="w-full bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
              data-testid="new-user-role"
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <button
          onClick={handleAddUser}
          disabled={addingUser}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg"
          data-testid="add-user-btn"
        >
          {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add User
        </button>
      </div>

      {users.length > 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5">
            <h3 className="text-white font-semibold">Portal Users ({users.length})</h3>
          </div>
          <div className="divide-y divide-white/5">
            {users.map(u => (
              <div key={u.id} className="flex items-center px-5 py-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold mr-3">
                  {u.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{u.name}</p>
                  <p className="text-white/30 text-xs font-mono">{u.email}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/40 mr-3">
                  {u.role?.replace('domain_', '')}
                </span>
                {u.role === 'domain_supervisor' && (
                  <span
                    className="text-[10px] mr-3 text-teal-300/70"
                    title="Agents this supervisor can see"
                    data-testid={`supervisor-scope-${u.id}`}
                  >
                    {u.assigned_agent_ids?.length
                      ? `${u.assigned_agent_ids.length} assigned`
                      : 'all domain agents'}
                  </span>
                )}
                <div
                  className="flex items-center gap-1.5 mr-4"
                  title={u.sms_access ? 'SMS sending enabled — click to disable' : 'SMS sending disabled — click to enable'}
                >
                  <MessageSquare className={`w-3.5 h-3.5 ${u.sms_access ? 'text-emerald-400' : 'text-white/15'}`} />
                  <button
                    onClick={() => toggleSmsAccess(u)}
                    role="switch"
                    aria-checked={!!u.sms_access}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${u.sms_access ? 'bg-emerald-500' : 'bg-white/10'}`}
                    data-testid={`sms-access-toggle-${u.id}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${u.sms_access ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                  </button>
                </div>
                <button
                  onClick={() => setEditingUser(u)}
                  className="text-white/20 hover:text-teal-300 transition-colors mr-2"
                  data-testid={`edit-user-${u.id}`}
                  title="Edit user"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeUser(u.id, u.email)}
                  className="text-white/20 hover:text-red-400 transition-colors"
                  data-testid={`remove-user-${u.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-white/20">
          <Users className="w-8 h-8 mx-auto mb-2" />
          <p>No portal users yet</p>
        </div>
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          domainId={domainId}
          agentPool={agentPool}
          headers={headers}
          onClose={() => setEditingUser(null)}
          onSaved={(updated) => {
            setUsers(u => (Array.isArray(u) ? u.map(x => x.id === updated.id ? updated : x) : [updated]));
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};


/**
 * EditUserModal — master-admin edits an existing portal user.
 *
 * Currently exposes:
 *  - **Full name** (rename)
 *  - **Role** (domain_admin / domain_supervisor / domain_agent)
 *  - **Assigned agents** — visible only when role=domain_supervisor.
 *    An empty checkbox list falls back to "supervisor sees ALL agents
 *    in this domain" so the safe default is discoverable without
 *    forcing the admin to tick 15 boxes.
 *
 * Extension + email are immutable via this modal (they're the login
 * identity — changes would need a separate flow).
 */
const EditUserModal = ({ user, domainId, agentPool, headers, onClose, onSaved }) => {
  const [name, setName] = useState(user.name || '');
  const [role, setRole] = useState(user.role || 'domain_agent');
  const [contactEmail, setContactEmail] = useState(user.contact_email || '');
  const [assigned, setAssigned] = useState(new Set(user.assigned_agent_ids || []));
  const [saving, setSaving] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const { startMasquerade } = useAuth();
  const navigate = useNavigate();

  const toggleAgent = (ext) => {
    setAssigned(prev => {
      const next = new Set(prev);
      if (next.has(ext)) next.delete(ext); else next.add(ext);
      return next;
    });
  };
  const toggleAll = () => {
    setAssigned(prev =>
      prev.size === agentPool.length ? new Set() : new Set(agentPool.map(a => a.extension))
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        role,
        contact_email: contactEmail.trim(),
        assigned_agent_ids: role === 'domain_supervisor' ? Array.from(assigned) : [],
      };
      const r = await axios.patch(
        `${API}/admin/domains/${domainId}/users/${user.id}`,
        body,
        { headers, timeout: 30000 },
      );
      toast.success('User updated');
      onSaved(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="bg-[#0A0F1C] border border-white/10 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0A0F1C]">
          <div>
            <h3 className="text-white font-semibold">Edit User</h3>
            <p className="text-white/30 text-xs font-mono">{user.email}</p>
          </div>
          <button onClick={onClose} data-testid="edit-user-close" className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-white/40 block mb-1">Full name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid="edit-user-name"
            />
          </div>

          <div>
            <label className="text-xs text-white/40 block mb-1">Contact email <span className="text-white/25">(real mailbox — used for temp-password emails)</span></label>
            <input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="their.name@company.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid="edit-user-contact-email"
            />
          </div>

          <div>
            <label className="text-xs text-white/40 block mb-1">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
              data-testid="edit-user-role"
            >
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {role === 'domain_supervisor' && (
            <div className="pt-2 border-t border-white/5" data-testid="edit-user-assigned-agents">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs text-white/60 font-semibold">Assigned agents</p>
                  <p className="text-[10px] text-white/30 leading-snug">
                    Tick the reps this supervisor is responsible for. Leave every box
                    unchecked to fall back to <span className="text-teal-300/70">all
                    agents in this domain</span>.
                  </p>
                </div>
                <button
                  onClick={toggleAll}
                  className="text-[10px] font-semibold text-teal-300 hover:text-teal-200 whitespace-nowrap"
                  data-testid="edit-user-toggle-all-agents"
                >
                  {assigned.size === agentPool.length ? 'Clear all' : 'Tick all'}
                </button>
              </div>
              {agentPool.length === 0 ? (
                <p className="text-xs text-white/30 italic py-3">
                  No domain agents in this tenant yet — assignments will appear here once you add some.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-lg border border-white/5 bg-black/20 divide-y divide-white/5">
                  {agentPool.map(a => {
                    const checked = assigned.has(a.extension);
                    return (
                      <label
                        key={a.id}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5"
                        data-testid={`assign-agent-${a.extension}`}
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-teal-500 border-teal-400' : 'border-white/20'}`}
                        >
                          {checked && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => toggleAgent(a.extension)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{a.name}</p>
                          <p className="text-[11px] text-white/30 font-mono">ext {a.extension}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-white/30 mt-2">
                {assigned.size === 0
                  ? '⓿ No specific assignment — will see all agents in this domain.'
                  : `● ${assigned.size} of ${agentPool.length} agents assigned.`}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/5">
          {tempPw ? (
            <div className="flex items-center gap-2 bg-teal-500/10 border border-teal-500/30 rounded-lg px-3 py-2" data-testid="admin-temp-password">
              <span className="text-[11px] text-teal-200">Temp password (shown once):</span>
              <code className="flex-1 font-mono text-xs text-white break-all">{tempPw}</code>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(tempPw); toast.success('Copied'); }}
                className="text-[11px] text-teal-300 hover:text-teal-100"
                data-testid="admin-temp-password-copy"
              >Copy</button>
            </div>
          ) : (
            <button
              type="button"
              disabled={resettingPw}
              onClick={async () => {
                if (!window.confirm(`Generate a temporary password for ${user.name || user.email}? Their current password stops working immediately.`)) return;
                setResettingPw(true);
                try {
                  const r = await axios.post(
                    `${API}/admin/domains/${user.domain_id || domainId}/users/${user.id}/reset-password`,
                    {}, { headers, timeout: 20000 },
                  );
                  setTempPw(r.data.temp_password);
                  toast.success('Password reset — user must change it on next login');
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Reset failed');
                } finally {
                  setResettingPw(false);
                }
              }}
              className="text-sm px-3 py-1.5 rounded-lg text-teal-300 hover:text-teal-200 hover:bg-teal-500/10 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="edit-user-reset-password"
              title="Generates a secure temporary password (shown once) and forces the user to set a new one on next login"
            >
              {resettingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Reset password
            </button>
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex justify-between gap-2 sticky bottom-0 bg-[#0A0F1C]">
          <button
            onClick={async () => {
              try {
                const r = await axios.post(
                  `${API}/admin/impersonate/${user.id}`,
                  {}, { headers, timeout: 15000 },
                );
                // Hand off to AuthContext.startMasquerade — this stashes
                // the admin token in a ref, swaps in the impersonation
                // JWT, and updates the `user` state so RBAC rebuilds
                // everywhere. The impersonation banner watches
                // `isMasquerading` and lets the admin exit at any time.
                startMasquerade(r.data.target, r.data.access_token);
                toast.success(`Viewing as ${r.data.target.name || r.data.target.email}`);
                onClose();
                // Client-side navigation only — a full-page reload wipes
                // the in-memory masquerade token and /auth/me would
                // restore the ORIGINAL admin session via the cookie.
                navigate('/');
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not start impersonation');
              }
            }}
            className="text-sm px-4 py-2 rounded-lg text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 inline-flex items-center gap-1.5"
            data-testid="edit-user-view-as"
            title="Start a short-lived session as this user to see exactly what they see"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            View as this user
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="text-sm px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="edit-user-cancel"
            >Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="text-sm font-semibold px-4 py-2 rounded-lg bg-teal-500 hover:bg-teal-400 text-white flex items-center gap-1.5 disabled:opacity-60"
              data-testid="edit-user-save"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
